import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import Loading from "@/components-legacy/loading";
import { PremiumFeature } from "@/components-legacy/premium-feature";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import useCreateShopApiKey from "@babackapp/private-api-client/client/use-create-shop-api-key";
import useDeleteShopApiKey from "@babackapp/private-api-client/client/use-delete-shop-api-key";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { PlusIcon } from "@radix-ui/react-icons";
import { PropsWithChildren } from "react";
import ApiKeyRow from "./api-key-row";
import CreateApiKeyDialog from "./create-api-key-dialog";

export default function APIKeyCard() {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();

  const { mutateAsync: createKey, isPending: isCreating } =
    useCreateShopApiKey(shop);
  const { mutateAsync: deleteKey, isPending: isDeleting } =
    useDeleteShopApiKey(shop);

  const hasAtLeastAdminRights = useViewerHasAtLeastAdminRights();
  if (!hasAtLeastAdminRights) return null;

  if (!shop.plan.api?.activated) return <DoesNotHavePlan />;

  const keys = shop.api.keys ?? [];

  return (
    <Container>
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-muted-foreground">
          {keys.length > 0
            ? `${keys.length} clé${keys.length > 1 ? "s" : ""}`
            : t("settings.integrations.api.noKeys")}
        </span>
        <CreateApiKeyDialog
          onSubmit={(label) =>
            createKey({ label: label || undefined })
          }
        >
          <Button size="sm" disabled={isCreating}>
            {isCreating ? (
              <Loading className="w-4 h-4" />
            ) : (
              <>
                <PlusIcon className="mr-2 size-4" />
                {t("settings.integrations.api.createKey")}
              </>
            )}
          </Button>
        </CreateApiKeyDialog>
      </div>

      {keys.length > 0 ? (
        <div>
          {keys.map((key) => (
            <ApiKeyRow
              key={key.id}
              apiKey={key}
              onDelete={(keyId) => void deleteKey(keyId)}
              isDeletePending={isDeleting}
            />
          ))}
        </div>
      ) : null}
    </Container>
  );
}

function Container({ children }: PropsWithChildren) {
  const t = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg leading-6 font-medium text-gray-900">
          {t("settings.integrations.api.title")}
        </CardTitle>
        <CardDescription>
          {t("settings.integrations.api.apiKeyDescription")}
        </CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function DoesNotHavePlan() {
  return (
    <Container>
      <PremiumFeature />
    </Container>
  );
}
